import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { isNative } from '@/shared/bridge';
import { UI_TEXT } from '@/shared/languages';
import { useNavigator } from '@/shared/navigation';
import { Button, useIsCompact, useSidebar } from '@/shared/ui';
import { useHeaderSlotRef } from '../model/header-actions';
import { MENU_TAB } from '../model/nav-config';
import { useRouteTitle } from '../model/use-route-title';
import { useDebugTap } from './debug-dialog';

/** 상위 경로 — 마지막 세그먼트 제거. 세그먼트가 하나뿐이면(최상위) null. 쿼리·해시는 버린다(상위 화면의 것이 아니다). */
function parentPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return null;
  return `/${segments.slice(0, -1).join('/')}`;
}

/**
 * 모바일(< md) 상단 네비 — iOS large-title 패턴.
 *  - **큰 타이틀**: 콘텐츠 흐름에 인라인(비-sticky) → 스크롤하면 데스크탑처럼 위로 사라진다. 좌측 정렬·조금 크게.
 *  - **sticky 바**: 큰 타이틀이 바 아래로 가려지면 작은 타이틀 + 배경(그라데이션·블러)이 서서히 등장/퇴장
 *    (IntersectionObserver 로 큰 타이틀 가림 여부 감지 → opacity transition). 좌=Up(상위 화면).
 *  - **페이지 액션 슬롯**: 큰 타이틀 우측(trailing) 같은 줄에 `HeaderActions` 포탈 대상(`registerSlot`) — 페이지의
 *    `Toolbar`(조회조건·다운로드)를 여기 얹는다(web 근간: a=헤더 trailing). 큰 타이틀과 함께 스크롤로 사라짐.
 * 타이틀 SSOT = route handle(`useRouteTitle`). 슬롯은 단일(app-layout 1 mount) — 동시 2페이지 mount(향후 애니
 * 아웃렛) 시 중복 포탈 가능성은 [[header-actions]] Directive 참조.
 */
export function AppHeader({ className }: { className?: string }) {
  const { title, isSubpage } = useRouteTitle();
  const navigator = useNavigator();
  const { pathname } = useLocation();
  // '<' = **Up**(상위 화면) — 히스토리 back 이 아니다. back 은 "직전에 있던 곳"이라 어디서 들어왔느냐에 따라
  // 목적지가 매번 달라지는데(대시보드에서 들어온 발전소 상세 vs 알림에서 들어온 발전소 상세), 화면 위계는 고정된
  // 사실이라 **항상 같은 곳**으로 올려보내는 편이 예측 가능하다. 상위 = 마지막 세그먼트 제거 — 하위 화면은 전부
  // `상위경로/식별자` 꼴이라 이 규칙 하나로 떨어진다(`/plants/:id/use` → `/plants/:id` 처럼 2차 하위도 자연히 맞다).
  // 딥링크로 곧장 진입해 앱 내 히스토리가 없어도 올라갈 곳이 명확하다(히스토리 깊이 판정 불요).
  const upPath = isSubpage ? parentPath(pathname) : null;
  // 페이지 액션(Toolbar) 포탈 대상 — 타이틀 줄 trailing(a=헤더 trailing, iOS large-title).
  const registerSlot = useHeaderSlotRef();
  // 히든 디버그 제스처 — 해당 화면(설정)에서만 콜백을 돌려준다(그 외 undefined → 그냥 텍스트).
  const onTitleTap = useDebugTap();
  // 전체 네비 진입점 — **브라우저 모바일 전용**. 네이티브는 하단 탭의 "메뉴"가, wide 는 좌측 rail 이 담당하므로
  // 둘 다 아닌 구간(비-네이티브 + compact)에만 햄버거가 필요하다. `isNative()` 는 브릿지 유무라 세션 내 불변.
  const showMenuButton = useIsCompact() && !isNative();
  const { setOpenMobile } = useSidebar();
  const headerRef = useRef<HTMLElement>(null);
  const largeTitleRef = useRef<HTMLHeadingElement>(null);
  const [compact, setCompact] = useState(false);

  // 큰 타이틀이 sticky 바(높이만큼 root 상단 축소) 아래로 완전히 가려지면 compact=true → 작은 타이틀·배경 fade-in.
  useEffect(() => {
    const target = largeTitleRef.current;
    const bar = headerRef.current;
    if (!target || !bar) {
      setCompact(false);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry) setCompact(!entry.isIntersecting);
      },
      { rootMargin: `-${bar.offsetHeight}px 0px 0px 0px`, threshold: 0 },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [title]);

  return (
    <>
      <header
        ref={headerRef}
        className="pointer-events-none fixed inset-x-0 top-0 z-30"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* 배경(그라데이션 + 블러) — compact 일 때만 서서히 등장. 그라데이션은 opacity 로 페이드(배경 transition 불가).
            바보다 아래로(-bottom-8) 더 키워 페이드를 길게 — 콘텐츠가 더 부드럽게 사라진다.
            from-* 은 **콘텐츠 면과 반드시 동일 토큰**(AppShell 의 bg-heviton-bg-tinted) — `--background`(흰색)를
            쓰면 tint 배경 위에 흰 띠가 뜬다. 콘텐츠 배경 토큰을 바꾸면 여기도 같이 바꿀 것. */}
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-x-0 top-0 -bottom-8 bg-gradient-to-b from-heviton-bg-tinted to-transparent transition-opacity duration-300 ${
            compact ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div className="relative flex h-11 items-center gap-1 px-2">
          {/* 중 — 작은 타이틀(중앙). 큰 타이틀 h1 의 시각적 중복이라 aria-hidden. */}
          <span
            aria-hidden
            className={`min-w-0 max-w-app-content flex-1 truncate text-center text-base font-semibold text-foreground transition-opacity duration-300 ${
              compact ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {title}
          </span>
        </div>
      </header>
      {/* spacer — fixed 바는 flow 에서 빠지므로 바 높이(safe-area + h-11=2.75rem)만큼 콘텐츠를 밀어준다. */}
      <div aria-hidden style={{ height: 'calc(env(safe-area-inset-top))' }} />
      {/* Up(상위) + 큰 타이틀 — 한 줄(콘텐츠 흐름, 비 fixed). 스크롤되어 바 아래로 사라진다. */}
      <div className={`flex items-center gap-2 ${className}`}>
        {/* 좌측 슬롯 — Up 과 햄버거가 **같은 자리를 공유**하고 하위 페이지에선 Up 이 이긴다
            (Material 의 nav drawer ↔ up arrow 규칙). 둘을 나란히 두면 좌측이 아이콘 2개로 붐빈다. */}
        {upPath ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={UI_TEXT.NAV_BACK}
            // replace — 올라가는 이동이라 방금 떠난 하위 화면을 히스토리에 남기지 않는다(push 면 상위↔하위가
            // 계속 쌓이고, 네이티브 스택 전환에선 '앞으로' 슬라이드가 붙어 방향이 거꾸로 보인다).
            onClick={() => navigator.replace(upPath)}
            className="pointer-events-auto shrink-0"
          >
            <ChevronLeft className="size-7" />
          </Button>
        ) : showMenuButton ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={MENU_TAB.label}
            onClick={() => setOpenMobile(true)}
            className="pointer-events-auto shrink-0 p-4"
          >
            <MENU_TAB.icon className="size-5" />
          </Button>
        ) : null}
        {title && (
          // 연타 제스처가 등록된 화면에선 select-none — 빠른 연타가 텍스트 드래그 선택으로 새지 않게.
          // 버튼 시맨틱은 일부러 안 준다(히든 제스처 — 스크린리더·포커스 순서에 노출하지 않음).
          <h1
            ref={largeTitleRef}
            onClick={onTitleTap}
            className={`flex items-center min-w-0 min-h-9 truncate text-2xl font-bold leading-snug tracking-tight text-foreground ${
              onTitleTap ? 'select-none' : ''
            }`}
          >
            {title}
          </h1>
        )}
        {/* 페이지 액션 slot — 타이틀 우측(trailing) 같은 줄. 페이지가 <HeaderActions> 로 Toolbar 포탈. 비면 0폭. */}
        <div
          ref={registerSlot}
          className="pointer-events-auto ml-auto flex shrink-0 items-center gap-1"
        />
      </div>
    </>
  );
}
